import { Modal, message } from 'antd';
import { createCheckoutSession } from 'services/api/users';
import { SESSION_PENDING_PRO_CHECKOUT } from 'utils/constants';

export const DOMAIN_ACCESS_ERROR = {
  NOT_VERIFIED: 'DOMAIN_NOT_VERIFIED',
  NOT_CLASSIFIED: 'DOMAIN_NOT_CLASSIFIED',
  NOT_APPROVED: 'DOMAIN_NOT_APPROVED',
  NO_METRICS: 'NO_METRICS',
};

export const getApiErrorCode = (error) =>
  error?.response?.data?.error || error?.response?.data?.code;

/**
 * Handles domain soft-access API errors.
 * @returns {boolean} true if the error was handled
 */
export function handleDomainAccessError(
  error,
  { proPlansEnabled = false, onNotVerified, onNotClassified } = {}
) {
  const code = getApiErrorCode(error);

  if (code === DOMAIN_ACCESS_ERROR.NOT_APPROVED) {
    if (proPlansEnabled) {
      Modal.confirm({
        title: 'Domain pending approval',
        content:
          'Your domain is still being reviewed. Wait for approval, or upgrade to Pro for full marketplace access now.',
        okText: 'Upgrade to Pro',
        cancelText: 'Wait for approval',
        onOk: async () => {
          try {
            const { data } = await createCheckoutSession();
            sessionStorage.setItem(SESSION_PENDING_PRO_CHECKOUT, '1');
            window.location.href = data.url;
          } catch (err) {
            message.error('Could not start checkout. Please try again.');
            throw err;
          }
        },
      });
    } else {
      message.warning(
        'Your domain is still being reviewed. This action will be available once approved.'
      );
    }
    return true;
  }

  if (code === DOMAIN_ACCESS_ERROR.NOT_VERIFIED) {
    message.error('Please verify domain ownership before continuing.');
    onNotVerified?.();
    return true;
  }

  if (code === DOMAIN_ACCESS_ERROR.NOT_CLASSIFIED) {
    message.error(
      'We could not analyze your website. Make sure it loads, then try again.'
    );
    onNotClassified?.();
    return true;
  }

  if (code === DOMAIN_ACCESS_ERROR.NO_METRICS) {
    // Pro writes should succeed with provisional DR after API deploy; keep a
    // soft message for any remaining edge cases.
    message.warning(
      'Domain metrics are still loading. Please wait a moment and try again.'
    );
    return true;
  }

  return false;
}

/** Normalize opportunities/list response (envelope or legacy array). */
export function parseOpportunitiesListResponse(data) {
  if (Array.isArray(data)) {
    return {
      opportunities: data,
      upgradeCta: false,
      metricsPending: false,
      previewLimit: null,
    };
  }

  if (!data || typeof data !== 'object') {
    return {
      opportunities: [],
      upgradeCta: false,
      metricsPending: false,
      previewLimit: null,
    };
  }

  // Envelope: { opportunities, upgradeCta, metricsPending, previewLimit }
  if (Array.isArray(data.opportunities)) {
    return {
      opportunities: data.opportunities,
      upgradeCta: Boolean(data.upgradeCta),
      metricsPending: Boolean(data.metricsPending),
      previewLimit: data.previewLimit ?? null,
    };
  }

  // Nested: { data: [...] } or { data: { opportunities, ... } }
  if (Array.isArray(data.data)) {
    return {
      opportunities: data.data,
      upgradeCta: Boolean(data.upgradeCta),
      metricsPending: Boolean(data.metricsPending),
      previewLimit: data.previewLimit ?? null,
    };
  }

  if (data.data && Array.isArray(data.data.opportunities)) {
    return {
      opportunities: data.data.opportunities,
      upgradeCta: Boolean(data.data.upgradeCta ?? data.upgradeCta),
      metricsPending: Boolean(
        data.data.metricsPending ?? data.metricsPending
      ),
      previewLimit: data.data.previewLimit ?? data.previewLimit ?? null,
    };
  }

  return {
    opportunities: [],
    upgradeCta: Boolean(data.upgradeCta),
    metricsPending: Boolean(data.metricsPending),
    previewLimit: data.previewLimit ?? null,
  };
}
